import React from "react";
import { motion } from "framer-motion";
import { 
  FaChartLine, 
  FaBullseye, 
  FaArrowRight,
  FaRocket,
  FaHandshake,
  FaBox,
  FaUsers,
  FaGlobe,
  FaLightbulb,
  FaStar,
  FaCode,
  FaMobileAlt,
  FaShieldAlt
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const About = () => {
  const milestones = [
    { 
      year: "2022", 
      title: "Agency Founded", 
      description: "Started as a solo developer focusing on Kenyan businesses", 
      icon: <FaRocket className="w-5 h-5" />, 
      highlight: true 
    },
    { 
      year: "2023", 
      title: "First Major Clients", 
      description: "Secured contracts with 10+ businesses across industries", 
      icon: <FaHandshake className="w-5 h-5" />, 
      highlight: true 
    },
    { 
      year: "2024", 
      title: "Package Development", 
      description: "Created industry-specific CRM/CMS solutions", 
      icon: <FaBox className="w-5 h-5" />, 
      highlight: true 
    },
    { 
      year: "2025", 
      title: "Team Expansion", 
      description: "Grew to 5 specialized developers and designers", 
      icon: <FaUsers className="w-5 h-5" /> 
    },
    { 
      year: "2026", 
      title: "Global Projects", 
      description: "Expanded to serve international clients", 
      icon: <FaGlobe className="w-5 h-5" /> 
    }
  ];

  const coreValues = [
    {
      icon: <FaLightbulb className="w-6 h-6" />,
      title: "Innovation First",
      description: "We constantly explore new technologies to deliver cutting-edge solutions"
    },
    {
      icon: <FaShieldAlt className="w-6 h-6" />,
      title: "Reliability",
      description: "Our systems are built to perform consistently and scale with your business"
    },
    {
      icon: <FaCode className="w-6 h-6" />,
      title: "Code Quality",
      description: "Clean, maintainable code that ensures long-term sustainability"
    },
    {
      icon: <FaMobileAlt className="w-6 h-6" />,
      title: "User-Centric",
      description: "Every solution is designed with the end-user experience in mind"
    }
  ];

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-[#010536] via-[#020a5c] to-[#000324]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(253,170,1,0.1)_0%,transparent_50%),radial-gradient(circle_at_80%_20%,rgba(99,102,241,0.1)_0%,transparent_50%)]" />
        <div className="absolute top-20 left-10 w-40 h-40 bg-[#fdaa01]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-52 h-52 bg-blue-500/10 rounded-full blur-3xl animate-float [animation-delay:5s]" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-[#fdaa01]/10 border border-[#fdaa01]/30 text-[#fdaa01] px-6 py-2.5 rounded-full text-sm font-semibold mb-6"
            >
              <span className="w-2 h-2 bg-[#fdaa01] rounded-full animate-pulse" />
              About Us
            </motion.span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4">
              About{' '}
              <span className="bg-gradient-to-r from-[#fdaa01] to-[#ffc042] bg-clip-text text-transparent">
                Shyee Codes
              </span>
            </h1>
            
            <div className="w-24 h-1 bg-gradient-to-r from-[#fdaa01] to-transparent mx-auto mb-8" />
            
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Empowering{' '}
              <span className="font-bold text-[#fdaa01]">Kenyan Businesses</span>
              {' '}with{' '}
              <span className="font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Tailored Digital Solutions
              </span>
            </p>
          </motion.div>
          
          {/* Main Description Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 sm:p-12 shadow-2xl border border-gray-100"
          >
            <p className="text-lg text-gray-700 leading-relaxed text-center mb-8">
              We specialize in custom CRM/CMS development, UI/UX design, and full-stack web applications 
              that drive growth and enhance digital presence.
            </p>
            
            {/* Mission Statement */}
            <div className="bg-[#fdaa01]/5 border-l-4 border-[#fdaa01] rounded-r-2xl p-6 sm:p-8 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <FaBullseye className="text-2xl text-[#fdaa01]" />
                <h3 className="text-xl font-bold text-[#010536]">Our Mission</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To bridge the gap between enterprise-grade software and accessible pricing for businesses of all sizes. 
                We believe in building more than just websites; we create{' '}
                <span className="font-bold text-[#010536]">digital foundations</span>
                {' '}that scale with your growth and adapt to your evolving needs.
              </p>
            </div>
            
            {/* Key Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                { value: '10+', label: 'Custom Systems', icon: <FaCode /> },
                { value: '14', label: 'Industry Packages', icon: <FaBox /> },
                { value: '100%', label: 'Client Retention', icon: <FaStar /> }
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + idx * 0.1 }}
                  className="bg-gradient-to-br from-[#010536] to-[#020a5c] rounded-2xl p-6 text-center hover:scale-105 transition-transform duration-300"
                >
                  <div className="text-[#fdaa01] text-2xl mb-2 flex justify-center">{stat.icon}</div>
                  <div className="text-3xl font-extrabold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-white/70">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            
            {/* CTA Button */}
            <div className="text-center">
              <Link to="/ready-systems">
                <motion.button
                  className="inline-flex items-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg shadow-[#fdaa01]/30 hover:shadow-xl hover:shadow-[#fdaa01]/40 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View All Systems
                  <FaArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story & Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Story Column */}
            <motion.div
              initial={{ x: -30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#fdaa01] mb-4 px-4 py-2 bg-[#fdaa01]/10 rounded-full border border-[#fdaa01]/20">
                Our Story
              </span>
              
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#010536] mb-6">
                From Code to Impact
              </h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-[#010536]">Shyee Codes</strong> began with a simple realization: Kenyan businesses 
                    needed more than just websites—they needed <em className="text-[#fdaa01] font-semibold">digital foundations</em> that could 
                    scale with their growth.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <p className="text-gray-700 leading-relaxed">
                    We started by building custom solutions for individual clients, but quickly 
                    recognized patterns across industries. This led us to develop our signature{' '}
                    <strong className="text-[#010536]">industry-specific packages</strong>—pre-built systems that combine 
                    best practices with custom flexibility.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <p className="text-gray-700 leading-relaxed">
                    Today, we bridge the gap between enterprise-grade software and accessible 
                    pricing, bringing sophisticated CRM/CMS solutions to businesses of all sizes.
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* Timeline Column */}
            <motion.div
              initial={{ x: 30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="bg-gradient-to-br from-[#010536] to-[#020a5c] rounded-3xl p-8 shadow-xl"
            >
              <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <FaChartLine className="text-[#fdaa01]" />
                Our Journey Timeline
              </h3>
              
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#fdaa01] via-[#fdaa01]/50 to-transparent" />
                
                <div className="space-y-8">
                  {milestones.map((milestone, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 }}
                      className={`relative flex items-start gap-4 ${
                        milestone.highlight ? 'bg-[#fdaa01]/10 rounded-2xl p-4 border border-[#fdaa01]/30' : ''
                      }`}
                    >
                      {/* Timeline dot */}
                      <div className="relative z-10 flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-[#fdaa01] to-[#ffc042] flex items-center justify-center text-[#010536] shadow-lg">
                        {milestone.icon}
                      </div>
                      
                      <div className="flex-1">
                        <div className="text-sm font-bold text-[#fdaa01] mb-1">
                          {milestone.year}
                        </div>
                        <h4 className="text-lg font-bold text-white mb-1">
                          {milestone.title}
                        </h4>
                        <p className="text-sm text-white/70 leading-relaxed">
                          {milestone.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#fdaa01] mb-4 px-4 py-2 bg-[#fdaa01]/10 rounded-full border border-[#fdaa01]/20">
              What Drives Us
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#010536]">
              Our Core Values
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#fdaa01] to-transparent mx-auto mt-4" />
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#fdaa01]"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#010536] to-[#020a5c] flex items-center justify-center text-[#fdaa01] mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-[#010536] mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#010536] to-[#020a5c] rounded-3xl p-12 shadow-2xl border border-[#fdaa01]/10"
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#fdaa01] mb-4">
              Ready to Start Your Digital Journey?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Let's build something amazing together
            </p>
            <Link to="/contact">
              <motion.button
                className="inline-flex items-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg shadow-[#fdaa01]/30 hover:shadow-xl hover:shadow-[#fdaa01]/40 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get In Touch
                <FaArrowRight className="ml-2" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;